/**
 * chip — quando a ligação em curso aparece como "chip" (a janelinha de voz) e não como a janela de vídeo
 * (desenho das sobras §11, 25/10/2026).
 *
 * Função pura, sem React nem LiveKit: o `Header` precisa saber SE há chip (para reservar a faixa dele no celular) e
 * não pode importar a janelinha inteira. A janelinha e o `Header` usam a MESMA regra, daqui: nunca há faixa sem chip,
 * nem chip sem faixa.
 *
 * Há chip em `chamando`, em `em_curso` (voz) e em `terminou` (voz e vídeo: os 3 s do desfecho). O vídeo atendido
 * (`em_curso`) é a `JanelaDeChamada`, tela cheia ou miniatura: não há chip e não há faixa.
 */

import type { LigacaoAtual } from "@/contexts/CallContext";

type FaseEMidia = Pick<LigacaoAtual, "midia" | "fase">;

/** Vídeo atendido, para a janelinha: em `em_curso` há id e início (a `JanelaDeChamada` precisa dos dois). */
export type VideoAtendido = Extract<LigacaoAtual, { fase: "em_curso" }> & { midia: "video" };

/** A regra, sem tipos: vídeo atendido é a única ligação ativa que NÃO vira chip. */
function ehVideoEmCurso(ligacao: FaseEMidia): boolean {
  return ligacao.midia === "video" && ligacao.fase === "em_curso";
}

/**
 * Vídeo atendido: a casca é a `JanelaDeChamada`. A mesma regra de `mostraOChip`, com o tipo estreitado para a
 * janelinha (o predicado nomeia só o vídeo: se fosse "em curso" qualquer, o ramo de fora perderia a voz em curso).
 */
export function ehVideoAtendido(ligacao: LigacaoAtual): ligacao is VideoAtendido {
  return ehVideoEmCurso(ligacao);
}

/** Há um chip na tela para esta ligação (e, no celular, a faixa que o `Header` reserva para ele). */
export function mostraOChip(ligacao: FaseEMidia | null): boolean {
  return ligacao !== null && !ehVideoEmCurso(ligacao);
}
